import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { InputField } from "@/components/input-field/InputField";
import {
  AsyncAutocompleteSelect,
  Option,
} from "@/components/select/AsyncAutocompleteSelect";
import { SelectComponent } from "@/components/select/Select";
import { AppRoutes } from "@/constants/routes";
import { useRoles } from "@/hooks/useRoles";
import { ActiveDirectoryDriverUser } from "@/services/drivers-ad.service";
import { AppColors } from "@/styles/colors";
import { isEmpty } from "@/utils/query-string";
import {
  Card,
  Field,
  Flex,
  Grid,
  GridItem,
  Text,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useRef, useState } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { driverUpdateSchema } from "../schema/drivers.schema";
import { DriverTruckCard } from "../create/DriverTruckCard";
import {
  fetchActiveTrucksHandler,
  fetchDefaultADUsersHandler,
  getADOptionsHandler,
  loadADOptionsHandler,
  submitUpdateDriverHandler,
  fetchDriverHandler,
} from "../schema/handler-functions";
import { Save } from "lucide-react";

type DriverEditForm = {
  usuario?: Option | null;
  nombreApellido?: string;
  email?: string;
  activo?: unknown;
  equipos: { equipoId?: unknown }[];
};

export const DriverEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { fetchPermissions } = useRoles();

  const [isActive, setIsActive] = useState(false);
  useEffect(() => {
    setIsActive(true);
  }, []);

  const { control, setValue, handleSubmit } = useForm<DriverEditForm>({
    resolver: zodResolver(driverUpdateSchema),
    defaultValues: {
      usuario: null,
      nombreApellido: "",
      email: "",
      activo: [],
      equipos: [{ equipoId: [] }],
    },
  });

  const watched = useWatch({ control });

  const {
    fields: truckFields,
    append: appendTruck,
    remove: removeTruck,
    replace: replaceTrucks,
  } = useFieldArray({
    control,
    name: "equipos",
  });

  const [loadingSave, setLoadingSave] = useState(false);
  const [loadingTrucks, setLoadingTrucks] = useState(false);
  const [truckOptions, setTruckOptions] = useState<
    { label: string; value: string }[]
  >([]);

  const [loadingADUsers, setLoadingADUsers] = useState(false);
  const [defaultADUsers, setDefaultADUsers] = useState<
    ActiveDirectoryDriverUser[]
  >([]);

  const [loadingDriver, setLoadingDriver] = useState(false);

  useEffect(() => {
    if (!isActive) return;
    fetchActiveTrucksHandler({
      setLoadingTrucks,
      setTruckOptions,
      navigate,
      fetchPermissions,
    });
  }, [isActive]);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadADOptions = (inputValue: string): Promise<Option[]> => {
    return loadADOptionsHandler({
      timeoutRef,
      inputValue,
      setLoadingADUsers,
      getADOptions: async (value) => getADOptionsHandler({ inputValue: value }),
    });
  };

  useEffect(() => {
    if (!isActive) return;
    fetchDefaultADUsersHandler({
      setLoadingADUsers,
      setDefaultADUsers,
    });
  }, [isActive]);

  useEffect(() => {
    if (!isActive || !id) return;

    fetchDriverHandler({
      id: Number(id),
      setLoadingDriver,
      setValue,
      replaceTrucks,
      navigate,
      fetchPermissions,
    });
  }, [isActive, id]);

  const handleCancel = () => {
    navigate(AppRoutes.DRIVERS);
  };

  const handleAddTruck = () => {
    appendTruck({ equipoId: [] });
  };

  const hasDuplicateTruckSelected = useMemo(() => {
    const selectedValues = (watched?.equipos ?? [])
      .map((t) => {
        const raw = t?.equipoId;
        const selected = Array.isArray(raw) ? raw?.[0] : raw;
        const str =
          selected !== undefined && selected !== null ? String(selected) : "";
        return str.trim();
      })
      .filter(Boolean);

    return new Set(selectedValues).size !== selectedValues.length;
  }, [watched?.equipos]);

  const hasEmptyTruck = useMemo(() => {
    const trucks = watched?.equipos ?? [];
    return trucks.some((t) => {
      const raw = t?.equipoId;
      const selected = Array.isArray(raw) ? raw?.[0] : raw;
      return isEmpty(selected);
    });
  }, [watched?.equipos]);

  const hasNoFilledValues = useMemo(() => {
    const name = watched?.nombreApellido;
    return isEmpty(name) || hasEmptyTruck;
  }, [watched?.nombreApellido, hasEmptyTruck]);

  const onSubmit = async (values: DriverEditForm) => {
    if (!id) return;
    await submitUpdateDriverHandler({
      id: Number(id),
      values,
      setLoadingSave,
      navigate,
      fetchPermissions,
    });
  };

  const defaultADOptions = useMemo(() => {
    return (
      defaultADUsers?.map((u) => ({
        label: `${u.displayName} - ${u.mail}`,
        value: u.id,
      })) ?? []
    );
  }, [defaultADUsers]);

  const loadingPage = loadingDriver || loadingTrucks || loadingADUsers;

  return (
    <Flex
      flexDir="column"
      paddingTop="20px"
      gap="30px"
      height="100%"
      minHeight={0}
      data-testid="drivers-edit-page"
    >
      <SectionHeader
        title="Maestro de fleteros"
        addButtonLabel="Guardar"
        addButtonIcon={<Save />}
        cancelButtonLabel="Cancelar"
        handleAdd={handleSubmit(onSubmit)}
        handleCancel={handleCancel}
        loading={loadingSave || loadingPage}
        disableAdd={
          hasNoFilledValues ||
          loadingSave ||
          loadingPage ||
          hasDuplicateTruckSelected
        }
      />

      {loadingPage ? null : (
        <Flex flex="1" minHeight={0} overflowY="auto" flexDir="column" gap="16px">
          <Card.Root maxW="full" borderRadius="8px">
            <Card.Body
              paddingX="48px"
              paddingY="30px"
              borderRadius="8px"
              border={`1px solid ${AppColors.WHITE_BORDER_1}`}
            >
              <Grid templateColumns="repeat(4, 1fr)" gap="32px">
                <GridItem colSpan={1}>
                  <Field.Root>
                    <Field.Label>
                      <Text
                        fontWeight={600}
                        whiteSpace="nowrap"
                        overflow="hidden"
                        textOverflow="ellipsis"
                      >
                        Usuario (Active Directory)
                      </Text>
                    </Field.Label>
                    <AsyncAutocompleteSelect
                      control={control as any}
                      defaultOptions={defaultADOptions}
                      loadOptions={loadADOptions}
                      name="usuario"
                      placeholder="Seleccionar"
                      isLoading={loadingADUsers}
                      closeMenuOnSelect
                      triggerWithLength={false}
                      disabled
                      cacheOptions={false}
                    />
                  </Field.Root>
                </GridItem>

                <GridItem colSpan={1}>
                  <InputField
                    control={control as any}
                    inputName="nombreApellido"
                    labelText="Nombre"
                    placeholder="Ingresar"
                    required
                    disabled
                    minHeight="40px"
                  />
                </GridItem>

                <GridItem colSpan={1}>
                  <SelectComponent
                    control={control as any}
                    name="activo"
                    items={[
                      { label: "Activo", value: true },
                      { label: "Inactivo", value: false },
                    ]}
                    required
                    labelText="Estado"
                    minHeight="40px"
                    disabled={loadingSave || loadingPage}
                  />
                </GridItem>
              </Grid>
            </Card.Body>
          </Card.Root>

          {truckFields.map((field, index) => {
            return (
              <DriverTruckCard
                key={field.id}
                index={index}
                control={control as any}
                currentEquipo={(watched?.equipos ?? [])?.[index] as any}
                watchedEquipos={(watched?.equipos ?? []) as any}
                truckOptions={truckOptions}
                truckFieldsLength={truckFields.length}
                loadingTrucks={loadingTrucks}
                loadingCreate={loadingSave || loadingPage}
                removeTruck={removeTruck}
                handleAddTruck={handleAddTruck}
              />
            );
          })}
        </Flex>
      )}
    </Flex>
  );
};

